import { styled } from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import React, { useEffect } from "react";

import type { Tag } from "../../../src/types/contents/tag";
import { getTags } from "../api";
import { auth } from "../auth";
import { toImageUrl, toVideoUrl } from "../cloudinary";

import { MultiSelect } from "./MultiSelect";

const VisuallyHiddenInput = styled("input")({
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type MediaWithPreviewProps = {
  type: "video" | "image";
  label: string;
  alt: string;
  mediaId: string | undefined;
  mediaPreview: File | null;
  setMediaPreview: (file: File | null) => void;
  overrideAble: boolean;
  mediaIdOverride: boolean;
  setMediaIdOverride: (override: boolean) => void;
};

export const MediaWithPreview = ({
  type,
  label,
  alt,
  mediaId,
  mediaPreview,
  setMediaPreview,
  overrideAble,
  mediaIdOverride,
  setMediaIdOverride,
}: MediaWithPreviewProps) => {
  const previewComponent =
    type === "image" ? (
      <img
        src={
          mediaPreview
            ? URL.createObjectURL(mediaPreview)
            : toImageUrl(mediaId ?? "")
        }
        alt={alt}
      />
    ) : (
      <video
        controls
        src={
          mediaPreview
            ? URL.createObjectURL(mediaPreview)
            : toVideoUrl(mediaId ?? "")
        }
        aria-label={alt}
      />
    );
  return (
    <>
      {previewComponent}
      <Stack direction="row" spacing={2}>
        <Button
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
        >
          {label}
          <VisuallyHiddenInput
            type="file"
            onChange={(event) => {
              if (event.target.files?.[0]) {
                setMediaPreview(event.target.files[0]);
              }
            }}
          />
        </Button>
        {overrideAble && (
          <FormControlLabel
            control={
              <Checkbox
                checked={mediaIdOverride}
                disabled={mediaPreview === null}
                onChange={(e) => setMediaIdOverride(e.currentTarget.checked)}
              />
            }
            label="Override existing"
          />
        )}
      </Stack>
    </>
  );
};

export const TagsSelector = ({
  value,
  onChange,
}: {
  value: string[];
  onChange: (selected: string[]) => void;
}) => {
  const [tags, setTags] = React.useState<Tag[]>([]);
  useEffect(() => {
    const init = async () => {
      setTags(await getTags());
    };

    if (auth.currentUser) void init();
  }, []);

  return (
    <MultiSelect
      label="Tags"
      value={value}
      options={tags
        .filter((tag) => !tag.hidden)
        .map((tag) => ({
          value: tag.key,
          label: tag.translations.fi,
        }))}
      onChange={onChange}
    />
  );
};
